import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { useI18n } from '@/lib/i18n'
import { openingAllocationLines } from '@/lib/opening-allocation'
import { GameStatusData } from '@/lib/types.ts'
import { ScrollText } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useMediaQuery } from 'usehooks-ts'
import { GameLogInMarkdown } from './GameLogInMarkdown.tsx'
import { GameLogWithTabs } from './GameLogWithTabs.tsx'
import { extractTextLogSpeechReferences } from './speech-references.ts'

export function GameLog({
  playerLog: rawPlayerLog = '...',
  playerJsonLog,
  playerLogIdentity = 'default-player',
  godLog: rawGodLog = '...',
  status,
  isGodView,
  preserveLiveRecord = false,
}: {
  playerLog: string
  playerJsonLog?: unknown | null
  playerLogIdentity?: string
  godLog?: string
  status?: GameStatusData
  isGodView?: boolean
  preserveLiveRecord?: boolean
}) {
  const { t } = useI18n()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const isCompleted = status?.game.status === 'completed'
  const isGuest = !status?.humanPlayer
  const showGodView = isGodView ?? (isGuest || isCompleted)

  const [cachedPlayerLog, setCachedPlayerLog] = useState(() => ({
    identity: playerLogIdentity,
    value: rawPlayerLog,
  }))
  const [godLog, setGodLog] = useState(rawGodLog)
  const playerLog = cachedPlayerLog.identity === playerLogIdentity
    ? cachedPlayerLog.value
    : rawPlayerLog

  useEffect(() => {
    setCachedPlayerLog((current) => {
      if (current.identity !== playerLogIdentity) {
        return { identity: playerLogIdentity, value: rawPlayerLog }
      }
      if (rawPlayerLog.length > current.value.length) {
        return { identity: playerLogIdentity, value: rawPlayerLog }
      }
      return current
    })
  }, [playerLogIdentity, rawPlayerLog])

  useEffect(() => {
    if (rawGodLog !== godLog) {
      setGodLog(rawGodLog)
    }
  }, [rawGodLog, godLog])

  const openingAllocation = !showGodView && status && !playerLog.trim()
    ? openingAllocationLines(status, t).join('\n\n')
    : ''
  const log = showGodView
    ? godLog
    : playerLog.trim() ? playerLog : openingAllocation
  const perspective = showGodView ? 'god' : 'player'
  const structuredLog = showGodView
    ? status?.game.godLogJson
    : playerJsonLog !== undefined
      ? playerJsonLog
      : status?.playerJsonLog
  const speechReferences = useMemo(
    () => extractTextLogSpeechReferences({
      structuredLog,
      renderedLog: log,
      gameId: status?.game.id,
      perspective,
      language: status?.game.language,
      players: status?.players.map(player => ({
        id: player.id,
        position: player.position,
      })),
    }),
    [
      structuredLog,
      log,
      status?.game.id,
      status?.game.language,
      status?.players,
      perspective,
    ],
  )
  const speechScopeKey = `${status?.game.id ?? 'unknown-game'}:${perspective}:${
    showGodView ? 'god' : playerLogIdentity
  }`
  const showTabs = isCompleted && !isMobile && !preserveLiveRecord

  return (
    <Card
      className="flex h-full flex-col border-border/50 bg-background/50 backdrop-blur-sm"
      data-testid="game-log"
      data-perspective={showGodView ? 'god' : 'player'}
      data-log-identity={showGodView ? 'god' : playerLogIdentity}
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg font-medium">
          <div className="flex items-center gap-2">
            <ScrollText className="size-5 text-primary" />
            {t('play.gameLog')}
          </div>
        </CardTitle>
      </CardHeader>
      {showTabs ? (
        <GameLogWithTabs
          log={log}
          summaryLog={showGodView ? status?.game.summaryLog ?? undefined : undefined}
          speechReferences={speechReferences}
          scopeKey={speechScopeKey}
        />
      ) : (
        <GameLogInMarkdown
          log={log}
          speechReferences={speechReferences}
          scopeKey={speechScopeKey}
        />
      )}
    </Card>
  )
}
